import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { ContactDetails, ContactOpportunity } from "@/hooks/useContactDetails";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, PlusCircle, X } from "lucide-react";
import { useAssistants } from "@/hooks/useAssistants";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useTeam } from "@/hooks/useTeam";
import { useTags } from "@/hooks/useTags";
import { usePipelines } from "@/hooks/usePipelines";
import { useContactManagement } from "@/hooks/useContactManagement";
import { TagSelectorModal } from "@/components/chat/TagSelectorModal";
import { EditOpportunityFullDialog, type OpportunityEditData } from "@/components/crm/EditOpportunityFullDialog";
import { WonOpportunityDialog } from "@/components/crm/WonOpportunityDialog";
import { LossReasonDialog } from "@/components/crm/LossReasonDialog";
import { OpportunityCard } from "./OpportunityCard";
import type { WonOpportunityData } from "@/hooks/useOpportunities";
import {
  formatCrmInteractionDateTime,
  logCrmContactInteraction,
} from "@/lib/crmInteractionLog";

type ContactSequenceState = {
  id: string;
  follow_up_id: string;
  status: string;
  follow_up?: { id: string; name: string } | null;
};

type AvailableSequence = {
  id: string;
  name: string;
  assistant_id: string;
  is_active: boolean;
};

const SEQUENCE_STATUS_LABELS: Record<string, { label: string; className: string }> = {
  active: {
    label: "Em cadência",
    className: "bg-[#DBEAFE] text-[#2563EB] dark:bg-blue-500/20 dark:text-[#60A5FA]",
  },
  paused: {
    label: "Pausado",
    className: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
  },
  blocked: {
    label: "Aguardando IA",
    className: "bg-slate-100 text-slate-600 dark:bg-neutral-500/15 dark:text-neutral-300",
  },
  goal_met: {
    label: "Meta atingida",
    className: "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400",
  },
  completed: {
    label: "Concluído",
    className: "bg-slate-100 text-slate-600 dark:bg-neutral-500/15 dark:text-neutral-300",
  },
  stopped: {
    label: "Encerrado",
    className: "bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400",
  },
};

interface ContactProps {
  contact?: ContactDetails;
  onUpdate?: (updates: Partial<ContactDetails>) => Promise<boolean>;
  onRefresh?: () => Promise<void>;
}

const Contact: React.FC<ContactProps> = ({ contact, onUpdate, onRefresh }) => {
  const { user } = useAuth();
  const { assistants } = useAssistants();
  const { members } = useTeam();
  const { tags: availableTags, fetchTags } = useTags();
  const { pipelines, pipelineStages } = usePipelines();
  const contactManagement = useContactManagement();

  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isSequenceModalOpen, setIsSequenceModalOpen] = useState(false);
  const [isOppEditModalOpen, setIsOppEditModalOpen] = useState(false);
  const [isCreateOppModalOpen, setIsCreateOppModalOpen] = useState(false);
  const [isExistingOppModalOpen, setIsExistingOppModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [attributionSearch, setAttributionSearch] = useState("");
  const [contactTags, setContactTags] = useState<string[]>([]);
  const [contactSequences, setContactSequences] = useState<ContactSequenceState[]>([]);
  const [availableSequences, setAvailableSequences] = useState<AvailableSequence[]>([]);
  const [selectedSequenceId, setSelectedSequenceId] = useState("");
  const [sequencesLoading, setSequencesLoading] = useState(false);
  const [newOppForm, setNewOppForm] = useState({
    title: '',
    pipeline_id: '',
    stage_id: '',
    product: '',
    price: '',
  });

  const [selectedOpportunity, setSelectedOpportunity] = useState<ContactOpportunity | null>(null);
  const [wonDialogOpen, setWonDialogOpen] = useState(false);
  const [lossDialogOpen, setLossDialogOpen] = useState(false);
  const [pendingEditData, setPendingEditData] = useState<OpportunityEditData | null>(null);

  useEffect(() => {
    setContactTags(contact?.tags || []);
  }, [contact?.tags]);

  const loadContactSequences = async (contactId: string, accountId: string) => {
    if (!supabase) return;
    setSequencesLoading(true);
    try {
      const [statesRes, followUpsRes] = await Promise.all([
        supabase
          .from("account_contact_follow_up_states")
          .select("id, follow_up_id, status, follow_up:account_assistant_follow_ups(id, name)")
          .eq("account_contact_id", contactId)
          .eq("account_id", accountId)
          .order("updated_at", { ascending: false }),
        supabase
          .from("account_assistant_follow_ups")
          .select("id, name, assistant_id, is_active")
          .eq("account_id", accountId)
          .eq("is_active", true)
          .order("name", { ascending: true }),
      ]);

      if (statesRes.error) throw statesRes.error;
      if (followUpsRes.error) throw followUpsRes.error;

      const stateRows = (statesRes.data || []) as Array<{
        id: string;
        follow_up_id: string;
        status: string;
        follow_up: { id: string; name: string } | { id: string; name: string }[] | null;
      }>;

      setContactSequences(
        stateRows.map((row) => ({
          id: row.id,
          follow_up_id: row.follow_up_id,
          status: row.status,
          follow_up: Array.isArray(row.follow_up) ? row.follow_up[0] ?? null : row.follow_up,
        }))
      );
      setAvailableSequences((followUpsRes.data || []) as AvailableSequence[]);
    } catch (error) {
      console.error("Erro ao carregar sequências do contato:", error);
    } finally {
      setSequencesLoading(false);
    }
  };

  useEffect(() => {
    if (!contact?.id || !user?.account_id) return;
    void loadContactSequences(contact.id, user.account_id);
  }, [contact?.id, user?.account_id]);

  if (!contact) return null;

  const handleApplyTags = async (newTags: string[]) => {
    const success = await contactManagement.setTags(contact.id, newTags);
    if (success) {
      setContactTags(newTags);
      onRefresh?.();
    }
  };

  const handleRemoveTag = async (tagToRemove: string) => {
    const success = await contactManagement.removeTag(contact.id, tagToRemove);
    if (success) {
      setContactTags(prev => prev.filter(t => t !== tagToRemove));
      onRefresh?.();
    }
  };

  const handleTagCreated = () => {
    fetchTags();
  };

  const handleAddToSequence = async () => {
    if (!supabase || !user?.account_id || !selectedSequenceId) return;

    const sequence = availableSequences.find((item) => item.id === selectedSequenceId);
    if (!sequence) return;

    setIsSaving(true);
    try {
      const existing = contactSequences.find((item) => item.follow_up_id === selectedSequenceId);

      if (existing && existing.status === "active") {
        alert("Este contato já está nesta sequência.");
        return;
      }

      if (existing) {
        const { error } = await supabase
          .from("account_contact_follow_up_states")
          .update({
            status: "active",
            assistant_id: sequence.assistant_id,
            stop_reason: null,
            next_interval_order: 0,
            cycle_count: 0,
            cycle_cooldown_until: null,
            next_fire_at: null,
            next_fire_reason: null,
            last_error: null,
            dispatch_locked_until: null,
          })
          .eq("id", existing.id);

        if (error) throw error;
      } else {
        const { error } = await supabase.from("account_contact_follow_up_states").insert({
          account_id: user.account_id,
          account_contact_id: contact.id,
          assistant_id: sequence.assistant_id,
          follow_up_id: sequence.id,
          status: "active",
        });

        if (error) throw error;
      }

      // Inclusão manual: limpa opt-out para a cadência poder disparar
      await supabase
        .from("account_contacts")
        .update({ follow_up_opt_out: false })
        .eq("id", contact.id);

      const userName = user.name || "Usuário";
      const when = formatCrmInteractionDateTime();
      await logCrmContactInteraction({
        accountId: user.account_id,
        contactId: contact.id,
        title: "Entrou na sequência",
        description: `${userName} adicionou o lead à sequência "${sequence.name}" em ${when}`,
      });

      setSelectedSequenceId("");
      setIsSequenceModalOpen(false);
      await loadContactSequences(contact.id, user.account_id);
      await onRefresh?.();
    } catch (error) {
      console.error("Erro ao adicionar contato à sequência:", error);
      alert("Não foi possível adicionar o contato à sequência.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleStopSequence = async (sequenceState: ContactSequenceState) => {
    if (!supabase || !user?.account_id) return;

    const sequenceName = sequenceState.follow_up?.name || "Sequência";
    if (!confirm(`Encerrar a sequência "${sequenceName}" para este lead?`)) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("account_contact_follow_up_states")
        .update({
          status: "stopped",
          stop_reason: "manual",
          next_fire_at: null,
          next_fire_reason: null,
          dispatch_locked_until: null,
        })
        .eq("id", sequenceState.id);

      if (error) throw error;

      const userName = user.name || "Usuário";
      const when = formatCrmInteractionDateTime();
      await logCrmContactInteraction({
        accountId: user.account_id,
        contactId: contact.id,
        title: "Saiu da sequência",
        description: `${userName} encerrou a sequência "${sequenceName}" em ${when}`,
      });

      await loadContactSequences(contact.id, user.account_id);
      await onRefresh?.();
    } catch (error) {
      console.error("Erro ao encerrar sequência:", error);
      alert("Não foi possível encerrar a sequência.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateOpportunity = async () => {
    if (!user?.account_id || !supabase || !contact) return;
    if (!newOppForm.title.trim() || !newOppForm.pipeline_id || !newOppForm.stage_id) return;

    setIsSaving(true);
    try {
      const priceValue = newOppForm.price ? parseFloat(newOppForm.price.replace(/\D/g, '')) / 100 : 0;
      const selectedStage = pipelineStages.find(s => s.id === newOppForm.stage_id);
      const stageType = selectedStage?.stage_type || 'normal';
      const derivedStatus = stageType === 'won' ? 'won' : stageType === 'lost' ? 'lost' : 'open';
      const { error } = await supabase
        .from('account_opportunities')
        .insert({
          account_id: user.account_id,
          contact_id: contact.id,
          pipeline_id: newOppForm.pipeline_id,
          stage_id: newOppForm.stage_id,
          title: newOppForm.title.trim(),
          product: newOppForm.product.trim() || null,
          price: priceValue,
          status: derivedStatus,
          tags: [],
          order_index: 0,
        });

      if (error) throw error;

      setIsCreateOppModalOpen(false);
      setNewOppForm({ title: '', pipeline_id: '', stage_id: '', product: '', price: '' });
      onRefresh?.();
    } catch (error) {
      console.error("Error creating opportunity:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditOpportunity = (opp: ContactOpportunity) => {
    setSelectedOpportunity(opp);
    setIsOppEditModalOpen(true);
  };

  const handleAddOpportunityClick = () => {
    const existingOpportunities = contact.opportunities || [];
    if (existingOpportunities.length > 0) {
      setIsExistingOppModalOpen(true);
      return;
    }
    setIsCreateOppModalOpen(true);
  };

  const handleConfirmEditExisting = () => {
    setIsExistingOppModalOpen(false);
    handleEditOpportunity(sortedOpportunities[0]);
  };

  const handleSaveOpportunityFull = async (data: OpportunityEditData) => {
    if (!user?.account_id || !selectedOpportunity || !supabase) return;

    const previousStatus = selectedOpportunity.status;

    if (data.status === 'won' && previousStatus !== 'won') {
      setPendingEditData(data);
      setIsOppEditModalOpen(false);
      setWonDialogOpen(true);
      return;
    }

    if (data.status === 'lost' && previousStatus !== 'lost') {
      setPendingEditData(data);
      setIsOppEditModalOpen(false);
      setLossDialogOpen(true);
      return;
    }

    await saveOpportunityUpdate(data);
  };

  const saveOpportunityUpdate = async (data: OpportunityEditData, extraData?: Record<string, unknown>) => {
    if (!user?.account_id || !selectedOpportunity || !supabase) return;

    const updateData: Record<string, unknown> = {
      title: data.title,
      pipeline_id: data.pipeline_id,
      stage_id: data.stage_id,
      product: data.product || null,
      price: data.price || 0,
      status: data.status,
      notes: data.notes || null,
      ...extraData,
    };

    if (data.status === 'won' || data.status === 'lost') {
      updateData.closed_at = data.closed_at || new Date().toISOString();
    } else {
      updateData.closed_at = null;
      updateData.sale_date = null;
      updateData.won_by_user_id = null;
      updateData.lost_by_user_id = null;
      updateData.loss_reason_id = null;
    }

    const { error } = await supabase
      .from('account_opportunities')
      .update(updateData)
      .eq('id', selectedOpportunity.id)
      .eq('account_id', user.account_id);

    if (error) throw error;

    setIsOppEditModalOpen(false);
    setSelectedOpportunity(null);
    setPendingEditData(null);
    onRefresh?.();
  };

  const handleWonConfirm = async (wonData: WonOpportunityData) => {
    if (!pendingEditData) return;
    await saveOpportunityUpdate(pendingEditData, {
      product: wonData.product || pendingEditData.product || null,
      price: wonData.price || pendingEditData.price || 0,
      sale_date: wonData.sale_date || null,
      won_by_user_id: wonData.won_by_user_id || null,
      notes: wonData.notes || pendingEditData.notes || null,
      loss_reason_id: null,
      lost_by_user_id: null,
    });
    setWonDialogOpen(false);
  };

  const handleLossConfirm = async (lossReasonId: string, lostByUserId?: string) => {
    if (!pendingEditData) return;
    await saveOpportunityUpdate(pendingEditData, {
      loss_reason_id: lossReasonId,
      lost_by_user_id: lostByUserId ?? null,
      sale_date: null,
      won_by_user_id: null,
    });
    setLossDialogOpen(false);
  };

  const handleAssistantToggle = async (checked: boolean) => {
    if (!onUpdate) return;
    await onUpdate({ assistant_is_active: checked });
    onRefresh?.();
  };

  const handleAssistantChange = async (assistantId: string) => {
    if (!onUpdate) return;
    await onUpdate({ assistant_id: assistantId === 'none' ? null : assistantId });
    onRefresh?.();
  };

  const handleAttributionChange = async (attributionId: string) => {
    if (!onUpdate) return;
    await onUpdate({ attribution_id: attributionId === 'none' ? null : attributionId });
    onRefresh?.();
  };

  const sortedOpportunities = [...(contact.opportunities || [])].sort((a, b) => {
    const statusOrder = { open: 0, won: 1, lost: 2 };
    const statusDiff = statusOrder[a.status] - statusOrder[b.status];
    if (statusDiff !== 0) return statusDiff;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const panelCard =
    "w-full p-4 gap-3 flex rounded-xl flex-col bg-white border border-[#E5E7EB] shadow-sm transition-colors duration-200 dark:bg-[#27272A] dark:border-[#3F3F46] dark:shadow-none";
  const panelTitle = "text-base font-semibold text-[#0F172A] dark:text-[#FAFAFA]";
  const selectTrigger =
    "w-full rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] transition-colors duration-200 focus:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:focus:border-[#60A5FA]";
  const emptyText = "text-sm text-[#94A3B8] dark:text-[#71717A]";
  const dialogShell =
    "rounded-2xl border border-[#E5E7EB] bg-white text-[#0F172A] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA]";
  const dialogInput =
    "h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]";
  const primaryBtn = "bg-[#2563EB] hover:bg-[#1D4ED8] text-white transition-colors duration-200";
  const outlineBtn =
    "rounded-xl border border-[#E5E7EB] bg-white text-[#334155] hover:bg-[#F1F5F9] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-transparent dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]";
  const tagClass =
    "capitalize flex items-center gap-1 pr-1 group cursor-pointer bg-[#DBEAFE] text-[#2563EB] border-0 hover:bg-[#BFDBFE] transition-colors duration-200 dark:bg-blue-500/20 dark:text-[#60A5FA] dark:hover:bg-blue-500/30";

  return (
    <div className="w-full flex flex-col gap-3 xl:order-3 order-2">
      {/* Assistant Section */}
      <div className={panelCard}>
        <div className="w-full flex justify-between items-center gap-2">
          <h3 className={panelTitle}>
            Assistente Ligado
          </h3>
          <Switch
            checked={contact.assistant_is_active}
            onCheckedChange={handleAssistantToggle}
          />
        </div>
        <Select
          value={contact.assistant_id || 'none'}
          onValueChange={handleAssistantChange}
        >
          <SelectTrigger className={selectTrigger}>
            <SelectValue placeholder="Selecione um assistente" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="none">Nenhum assistente</SelectItem>
              {assistants.filter(a => a.is_active).map((assistant) => (
                <SelectItem key={assistant.id} value={assistant.id}>
                  {assistant.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      {/* Attribution Section */}
      <div className={panelCard}>
        <h3 className={panelTitle}>Responsável pelo Contato</h3>
        <Select
          value={contact.attribution_id || 'none'}
          onValueChange={handleAttributionChange}
          onOpenChange={(open) => {
            if (!open) setAttributionSearch('');
          }}
        >
          <SelectTrigger className={selectTrigger}>
            <SelectValue placeholder="Selecione um responsável" />
          </SelectTrigger>
          <SelectContent>
            <div className="px-2 pb-2">
              <Input
                placeholder="Pesquisar responsável..."
                value={attributionSearch}
                onChange={(e) => setAttributionSearch(e.target.value)}
                className="h-8 rounded-lg border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <SelectGroup>
              <SelectItem value="none">Sem responsável</SelectItem>
              {members
                .filter(m => m.is_active && m.auth_user_id)
                .filter(m => {
                  if (!attributionSearch) return true;
                  const searchLower = attributionSearch.toLowerCase();
                  const name = (m.name || '').toLowerCase();
                  const email = (m.email || '').toLowerCase();
                  return name.includes(searchLower) || email.includes(searchLower);
                })
                .map((member) => (
                  <SelectItem key={member.auth_user_id} value={member.auth_user_id!}>
                    {member.name || member.email}
                  </SelectItem>
                ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      {/* Opportunities Section */}
      <div className={panelCard}>
        <div className="w-full flex justify-between items-center">
          <h3 className={panelTitle}>Oportunidades</h3>
          <button
            onClick={handleAddOpportunityClick}
            className="text-[#64748B] hover:text-[#0F172A] transition-colors duration-200 dark:text-[#A1A1AA] dark:hover:text-[#FAFAFA]"
            aria-label="Adicionar oportunidade"
          >
            <PlusCircle className="w-6 h-6" />
          </button>
        </div>
        <div className="flex flex-col gap-3 w-full">
          {sortedOpportunities.length > 0 ? (
            sortedOpportunities.map((opp) => (
              <OpportunityCard
                key={opp.id}
                opportunity={opp}
                onEdit={handleEditOpportunity}
              />
            ))
          ) : (
            <p className={emptyText}>Nenhuma oportunidade</p>
          )}
        </div>
      </div>

      {/* Tags Section */}
      <div className={panelCard}>
        <h3 className={panelTitle}>Tags</h3>
        <div className="flex flex-wrap gap-2 w-full">
          <Badge
            variant="default2"
            className="bg-[#F1F5F9] text-[#334155] hover:bg-[#EEF4FF] hover:text-[#2563EB] flex items-center cursor-pointer px-2 py-1 border border-[#E5E7EB] transition-colors duration-200 dark:bg-[#3F3F46] dark:text-[#D4D4D8] dark:border-[#3F3F46] dark:hover:bg-[rgba(37,99,235,.18)] dark:hover:text-[#60A5FA]"
            onClick={() => setIsTagModalOpen(true)}
          >
            <Plus className="w-4 h-4 mr-1" />
            <span>Adicionar</span>
          </Badge>

          {contactTags.map((tag) => (
            <Badge
              key={tag}
              className={tagClass}
            >
              <span>{tag}</span>
              <button
                onClick={() => handleRemoveTag(tag)}
                className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
                disabled={contactManagement.updating}
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}

          {contactTags.length === 0 && (
            <p className="text-xs text-[#94A3B8] dark:text-[#71717A]">Nenhuma tag atribuída</p>
          )}
        </div>
      </div>

      {/* Campaigns Section */}
      <div className={panelCard}>
        <div className="w-full flex justify-between items-center">
          <h3 className={panelTitle}>Campanhas</h3>
        </div>
        <div className="flex flex-wrap gap-2 w-full">
          {contact.campaigns && contact.campaigns.length > 0 ? (
            contact.campaigns.map((campaign, index) => (
              <Badge
                key={index}
                className="py-1 px-3 bg-[#DBEAFE] text-[#2563EB] border-0 transition-colors duration-200 dark:bg-blue-500/20 dark:text-[#60A5FA]"
              >
                {campaign}
              </Badge>
            ))
          ) : (
            <p className={emptyText}>Nenhuma campanha</p>
          )}
        </div>
      </div>

      {/* Sequences Section */}
      <div className={panelCard}>
        <div className="w-full flex justify-between items-center">
          <h3 className={panelTitle}>Sequências</h3>
          <button
            onClick={() => {
              setSelectedSequenceId("");
              setIsSequenceModalOpen(true);
            }}
            className="text-[#64748B] hover:text-[#0F172A] transition-colors duration-200 dark:text-[#A1A1AA] dark:hover:text-[#FAFAFA]"
            aria-label="Adicionar a sequência"
          >
            <PlusCircle className="w-6 h-6" />
          </button>
        </div>
        <div className="flex flex-col gap-2 w-full">
          {sequencesLoading ? (
            <p className={emptyText}>Carregando sequências...</p>
          ) : contactSequences.length > 0 ? (
            contactSequences.map((sequenceState) => {
              const statusMeta =
                SEQUENCE_STATUS_LABELS[sequenceState.status] || SEQUENCE_STATUS_LABELS.stopped;
              const canStop = ["active", "paused", "blocked"].includes(sequenceState.status);
              return (
                <div
                  key={sequenceState.id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-[#E5E7EB] bg-[#F8FAFC] px-3 py-2 dark:border-[#3F3F46] dark:bg-[#18181B]"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">
                      {sequenceState.follow_up?.name || "Sequência"}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusMeta.className}`}
                    >
                      {statusMeta.label}
                    </span>
                    {canStop && (
                      <button
                        type="button"
                        onClick={() => void handleStopSequence(sequenceState)}
                        disabled={isSaving}
                        className="rounded-md p-1 text-[#64748B] transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:text-[#A1A1AA] dark:hover:bg-red-500/15 dark:hover:text-red-400"
                        aria-label={`Encerrar sequência ${sequenceState.follow_up?.name || ""}`}
                        title="Encerrar sequência"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <p className={emptyText}>Nenhuma sequência</p>
          )}
        </div>
      </div>

      <TagSelectorModal
        open={isTagModalOpen}
        onOpenChange={setIsTagModalOpen}
        availableTags={availableTags}
        selectedTags={contactTags}
        onApplyTags={handleApplyTags}
        onTagCreated={handleTagCreated}
      />

      {/* Sequence Modal */}
      <Dialog
        open={isSequenceModalOpen}
        onOpenChange={(open) => {
          setIsSequenceModalOpen(open);
          if (!open) setSelectedSequenceId("");
        }}
      >
        <DialogContent className={dialogShell}>
          <DialogHeader>
            <DialogTitle className="text-[#0F172A] dark:text-[#FAFAFA]">
              Adicionar à sequência
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="sequence-select" className="text-[#0F172A] dark:text-[#FAFAFA]">
                Sequência
              </Label>
              <Select value={selectedSequenceId} onValueChange={setSelectedSequenceId}>
                <SelectTrigger id="sequence-select" className={selectTrigger}>
                  <SelectValue placeholder="Selecione uma sequência" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {availableSequences.length > 0 ? (
                      availableSequences.map((sequence) => (
                        <SelectItem key={sequence.id} value={sequence.id}>
                          {sequence.name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="__none__" disabled>
                        Nenhuma sequência ativa
                      </SelectItem>
                    )}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setIsSequenceModalOpen(false)}
                className={outlineBtn}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleAddToSequence}
                disabled={!selectedSequenceId || isSaving || availableSequences.length === 0}
                className={primaryBtn}
              >
                {isSaving ? "Adicionando..." : "Adicionar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Existing Opportunity Confirmation Modal */}
      <Dialog open={isExistingOppModalOpen} onOpenChange={setIsExistingOppModalOpen}>
        <DialogContent className={dialogShell}>
          <DialogHeader>
            <DialogTitle className="text-[#0F172A] dark:text-[#FAFAFA]">Oportunidade já existente</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-[#334155] dark:text-[#D4D4D8]">
              Este contato já possui uma oportunidade. Deseja editar a oportunidade existente?
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setIsExistingOppModalOpen(false)}
                className={outlineBtn}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmEditExisting}
                className={primaryBtn}
              >
                Editar existente
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Opportunity Modal */}
      <Dialog open={isCreateOppModalOpen} onOpenChange={setIsCreateOppModalOpen}>
        <DialogContent className={`${dialogShell} max-w-lg max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle className="text-[#0F172A] dark:text-[#FAFAFA]">Nova Oportunidade</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label className="text-[#0F172A] dark:text-[#FAFAFA]">Título *</Label>
              <Input
                value={newOppForm.title}
                onChange={(e) => setNewOppForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Título da oportunidade"
                className={dialogInput}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-2">
                <Label className="text-[#0F172A] dark:text-[#FAFAFA]">Pipeline *</Label>
                <Select
                  value={newOppForm.pipeline_id}
                  onValueChange={(value) => {
                    setNewOppForm(prev => ({ ...prev, pipeline_id: value, stage_id: '' }));
                  }}
                >
                  <SelectTrigger className={selectTrigger}>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {pipelines.map((pipeline) => (
                        <SelectItem key={pipeline.id} value={pipeline.id}>
                          {pipeline.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <Label className="text-[#0F172A] dark:text-[#FAFAFA]">Etapa *</Label>
                <Select
                  value={newOppForm.stage_id}
                  onValueChange={(value) => setNewOppForm(prev => ({ ...prev, stage_id: value }))}
                  disabled={!newOppForm.pipeline_id}
                >
                  <SelectTrigger className={selectTrigger}>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {pipelineStages
                        .filter(s => s.pipeline_id === newOppForm.pipeline_id)
                        .map((stage) => (
                          <SelectItem key={stage.id} value={stage.id}>
                            {stage.name}
                          </SelectItem>
                        ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-[#0F172A] dark:text-[#FAFAFA]">Produto/Serviço</Label>
              <Input
                value={newOppForm.product}
                onChange={(e) => setNewOppForm(prev => ({ ...prev, product: e.target.value }))}
                placeholder="Nome do produto ou serviço"
                className={dialogInput}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-[#0F172A] dark:text-[#FAFAFA]">Valor (R$)</Label>
              <Input
                value={newOppForm.price}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/\D/g, '');
                  if (!rawValue) { setNewOppForm(prev => ({ ...prev, price: '' })); return; }
                  const num = parseInt(rawValue, 10) / 100;
                  setNewOppForm(prev => ({ ...prev, price: num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }));
                }}
                placeholder="0,00"
                className={dialogInput}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setIsCreateOppModalOpen(false)}
                className={outlineBtn}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreateOpportunity}
                disabled={!newOppForm.title.trim() || !newOppForm.pipeline_id || !newOppForm.stage_id || isSaving}
                className={primaryBtn}
              >
                {isSaving ? "Criando..." : "Criar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Opportunity Edit Modal */}
      <EditOpportunityFullDialog
        open={isOppEditModalOpen}
        onClose={() => {
          setIsOppEditModalOpen(false);
          setSelectedOpportunity(null);
        }}
        onSave={handleSaveOpportunityFull}
        opportunity={selectedOpportunity}
      />

      {/* Won Opportunity Dialog */}
      <WonOpportunityDialog
        open={wonDialogOpen}
        onClose={() => {
          setWonDialogOpen(false);
          setPendingEditData(null);
        }}
        onConfirm={handleWonConfirm}
        opportunityTitle={selectedOpportunity?.title}
        initialProduct={pendingEditData?.product}
        initialPrice={pendingEditData?.price}
      />

      {/* Loss Reason Dialog */}
      <LossReasonDialog
        open={lossDialogOpen}
        onClose={() => {
          setLossDialogOpen(false);
          setPendingEditData(null);
        }}
        onConfirm={handleLossConfirm}
        opportunityTitle={selectedOpportunity?.title}
      />
    </div>
  );
};

export default Contact;
